import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { TextField, Toggle, Section, Row, SaveBar, EyebrowSizeField } from '@/admin/AdminUI';
import ImageUploader from '@/admin/ImageUploader';

const DEFAULT_PILLARS = [
  { label: '', body: '' },
  { label: '', body: '' },
  { label: '', body: '' },
];

export default function FastTrackTab() {
  const [ft, setFt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/admin/settings/fast-track').then((r) => {
      const d = r.data || {};
      // Ensure pillars is always exactly 3 items so the form renders cleanly
      const pillars = Array.isArray(d.pillars) && d.pillars.length === 3
        ? d.pillars
        : DEFAULT_PILLARS;
      setFt({ ...d, pillars });
    });
  }, []);

  if (!ft) return <div className="font-body text-ink-muted">Loading…</div>;

  const set = (k, v) => setFt({ ...ft, [k]: v });
  const setPillar = (idx, key, v) => {
    const next = ft.pillars.map((p, i) => (i === idx ? { ...p, [key]: v } : p));
    setFt({ ...ft, pillars: next });
  };

  // v3.15 — Eyebrow size commit handler.
  // Fast Track is defended twice: this validator refuses to send a bad
  // request, and the strict FastTrackSettings Pydantic model on the
  // server rejects any residual bool/string/decimal/out-of-range with
  // HTTP 422. Clearing removes `eyebrow_size` from the local state so
  // Pydantic sees an absent field and its default (None) is applied.
  const commitEyebrowSize = (n) => {
    if (n === null) {
      const next = { ...ft };
      delete next.eyebrow_size;
      setFt(next);
    } else {
      setFt({ ...ft, eyebrow_size: n });
    }
  };

  const save = async () => {
    setSaving(true); setSaved(false); setError('');
    try {
      await api.put('/admin/settings/fast-track', ft);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Save failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setTimeout(() => setError(''), 4000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="admin-fast-track-tab">
      <Section title="Fast Track — Section Header">
        <Row>
          <TextField label="Eyebrow" value={ft.eyebrow || ''} onChange={(v) => set('eyebrow', v)} testid="ft-eyebrow" />
          <TextField label="Headline" value={ft.headline || ''} onChange={(v) => set('headline', v)} testid="ft-headline" />
        </Row>
        <Row>
          <TextField label="Dek" value={ft.dek || ''} onChange={(v) => set('dek', v)} multiline testid="ft-dek" />
        </Row>
        <Row>
          <TextField label="Intro Paragraph" value={ft.intro_paragraph || ''} onChange={(v) => set('intro_paragraph', v)} multiline testid="ft-intro" />
        </Row>
        <EyebrowSizeField
          value={ft.eyebrow_size}
          onCommit={commitEyebrowSize}
          testid="fast-track-eyebrow-size"
          hint="Controls only the orange EYEBROW label above the Fast Track headline."
        />
      </Section>

      <Section title="Three Pillars">
        {[0, 1, 2].map((i) => (
          <Row key={i}>
            <TextField label={`Pillar ${i + 1} Label`} value={ft.pillars[i]?.label || ''} onChange={(v) => setPillar(i, 'label', v)} testid={`ft-pillar-${i}-label`} />
            <TextField label={`Pillar ${i + 1} Body`} value={ft.pillars[i]?.body || ''} onChange={(v) => setPillar(i, 'body', v)} multiline testid={`ft-pillar-${i}-body`} />
          </Row>
        ))}
      </Section>

      <Section title="Global Reach & CTAs">
        <Row>
          <TextField label="Global Reach Line" value={ft.global_reach_line || ''} onChange={(v) => set('global_reach_line', v)} multiline testid="ft-global-reach" />
        </Row>
        <Row>
          <TextField label="Primary CTA Label" value={ft.primary_cta_label || ''} onChange={(v) => set('primary_cta_label', v)} testid="ft-primary-cta-label" />
          <TextField label="Primary CTA URL (relative path or http URL)" value={ft.primary_cta_url || ''} onChange={(v) => set('primary_cta_url', v)} testid="ft-primary-cta-url" />
        </Row>
        <Row>
          <TextField label="Secondary Prompt" value={ft.secondary_prompt || ''} onChange={(v) => set('secondary_prompt', v)} testid="ft-secondary-prompt" />
        </Row>
        <Row>
          <TextField label="Secondary CTA Label" value={ft.secondary_cta_label || ''} onChange={(v) => set('secondary_cta_label', v)} testid="ft-secondary-cta-label" />
          <TextField label="Secondary CTA Email" value={ft.secondary_cta_email || ''} onChange={(v) => set('secondary_cta_email', v)} testid="ft-secondary-cta-email" />
        </Row>
      </Section>

      <Section title="Visibility">
        <Toggle label="Section Visible" value={!!ft.visible} onChange={(v) => set('visible', v)} testid="ft-visible" />
      </Section>

      <Section title="Background Image">
        <ImageUploader value={ft.background_image || ''} onChange={(v) => set('background_image', v)} label="Background Image" testid="ft-background-image" />
        <p className="mt-2 font-body text-[12px] text-ink-muted leading-relaxed max-w-xl">
          Background image for the section. Renders behind a dark scrim for legibility. Upload your own or paste a URL. Defaults to a placeholder cityscape if blank.
        </p>
      </Section>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-sm text-[13px]" data-testid="ft-error">{error}</div>
      )}

      <SaveBar onSave={save} saving={saving} saved={saved} />
    </div>
  );
}
